import { ReactNode, useState, useEffect } from 'react';
import { Button } from '@/ui-components/ui/button';
import { Input } from '@/ui-components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Grid3X3, 
  List, 
  ChevronDown,
  SlidersHorizontal,
  ListChecks,
  Trash2,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/util-components/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/ui-components/ui/sheet';
import { useDataView } from './data-view';
import { AdvancedFilterSortPanel } from './components/advanced-filter-sort-panel';
import { AdvancedState, SortParam } from './components/advanced-filter-types';
import { PermissionFeatureWrapper } from '@/util-components/auth/PermissionFeatureWrapper';
import { Permission } from '@/lib/api/interfaces/permissions.interface';
import { PlanKey } from '@/lib/api/interfaces/billing.interface';

// --- Types ---

export interface FilterParam {
  key: string;
  label: string;
  value: any;
}

interface DataViewToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;
  
  filterParams?: FilterParam[];
  selectedFilter?: any;
  onFilterChange?: (value: any) => void;
  filterLabel?: string;
  
  infoText?: ReactNode;
  leading?: ReactNode; // e.g. "Create" button
  trailing?: ReactNode; // Custom extra controls
  
  searchDebounceMs?: number;
  hideViewModeToggle?: boolean;

  // --- Advanced Filter Props ---
  mode?: 'simple' | 'advanced';
  onModeChange?: (mode: 'simple' | 'advanced') => void;
  
  advancedFilterDefs?: import('./components/advanced-filter-types').AdvancedFilterDef[];
  advancedSortDefs?: import('./components/advanced-filter-types').AdvancedSortDef[];
  advancedState?: import('./components/advanced-filter-types').AdvancedState;
  onAdvancedStateChange?: (state: import('./components/advanced-filter-types').AdvancedState) => void;
  onAdvancedReset?: () => void;
  defaultSort?: SortParam;
  
  // Multi-select bulk actions
  selectedIds?: string[];
  onSelectionDelete?: () => void;
  customBulkActions?: {
    label: string;
    icon: ReactNode;
    onClick: () => void;
  }[];
}

/**
 * Reusable toolbar for DataView.
 * Handles extensive responsive logic (Mobile Sheet vs Desktop Dropdown).
 */
export function DataViewToolbar({
  searchQuery,
  onSearchChange,
  searchPlaceholder = "Suchen...",
  filterParams,
  selectedFilter,
  onFilterChange,
  filterLabel,
  infoText,
  leading,
  trailing,
  searchDebounceMs = 500,
  hideViewModeToggle = false,
  
  mode = 'simple',
  onModeChange,
  advancedFilterDefs,
  advancedSortDefs,
  advancedState,
  onAdvancedStateChange,
  onAdvancedReset,
  defaultSort,
  selectedIds: propsSelectedIds,
  onSelectionDelete: propsOnSelectionDelete,
  customBulkActions
}: DataViewToolbarProps) {
  const { 
    viewMode, 
    onViewModeChange,
    isMultiSelectMode,
    setIsMultiSelectMode,
    selectedIds: contextSelectedIds,
    onMultiSelect,
    onSelectionDelete: contextOnSelectionDelete,
    clearSelection
  } = useDataView();

  const selectedIds = propsSelectedIds || contextSelectedIds;
  const onSelectionDelete = propsOnSelectionDelete || contextOnSelectionDelete;
  
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery);

  // Sync external searchQuery into local input state
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Debounce change dispatch to parent
  useEffect(() => {
    const id = setTimeout(() => {
      if (localQuery !== searchQuery) {
        onSearchChange(localQuery);
      }
    }, searchDebounceMs);
    return () => clearTimeout(id);
  }, [localQuery, onSearchChange, searchDebounceMs, searchQuery]);

  const getSelectedFilterLabel = () => {
    if (!filterParams || !selectedFilter) return filterLabel || "Filter";
    const selectedParam = filterParams.find(param => param.value === selectedFilter);
    return selectedParam?.label || filterLabel || "Filter";
  };

  const handleFilterSelect = (value: any) => {
    onFilterChange?.(value);
    setIsMobileFilterOpen(false);
  };

  return (
    <div className="bg-white flex-shrink-0 border-b border-gray-100/80 overflow-hidden relative grid">
      <AnimatePresence>
        {isMultiSelectMode ? (
          <motion.div
            key="multi-select"
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10, transition: { duration: 0.15, ease: "easeOut" } }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="w-full col-start-1 row-start-1"
          >
            <div className="w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between px-3 py-2 sm:px-6 sm:py-3">
                <div className="flex items-center gap-2 sm:gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                    className="h-9 px-2 sm:px-3 text-sm text-gray-600 hover:text-gray-900 bg-gray-50/50 hover:bg-gray-100 rounded-xl transition-all"
                  >
                    <X className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Auswahl aufheben</span>
                  </Button>
                  <div className="h-4 w-[1px] bg-gray-200 mx-0.5 sm:mx-1"></div>
                  <motion.span 
                    key={selectedIds.length}
                    initial={{ scale: 1.2, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-sm font-medium text-gray-700"
                  >
                    {selectedIds.length} <span className="hidden sm:inline">markiert</span>
                  </motion.span>
                </div>
                
                <div className="flex items-center gap-2 sm:gap-4">
                  {customBulkActions?.map((action, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={action.onClick}
                      disabled={selectedIds.length === 0}
                      className="h-9 px-3 sm:px-4 text-sm text-gray-700 border-gray-200 hover:bg-gray-50 hover:text-gray-900 rounded-xl transition-all font-medium"
                    >
                      {action.icon}
                      <span className="hidden sm:inline">{action.label}</span>
                    </Button>
                  ))}
                  
                  {onSelectionDelete && (
                    <PermissionFeatureWrapper
                      requiredPermission={Permission.MANAGE_ENTITIES}
                      requiredPlan={PlanKey.BASIC}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onSelectionDelete}
                        disabled={selectedIds.length === 0}
                        className="h-9 px-3 sm:px-4 text-sm text-red-600 border-gray-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 rounded-xl transition-all font-medium"
                      >
                        <Trash2 className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Löschen</span>
                      </Button>
                    </PermissionFeatureWrapper>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="standard"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10, transition: { duration: 0.15, ease: "easeOut" } }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="w-full col-start-1 row-start-1"
          >
            <div className="w-full max-w-7xl mx-auto">
            {/* Desktop Layout (lg and up) */}
      <div className="hidden lg:block px-6 py-3">
        <div className="flex items-center justify-between gap-6">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              placeholder={searchPlaceholder}
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              className="pl-10 h-9 text-sm bg-gray-50/50 border-gray-200 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-400 focus-visible:bg-white rounded-xl transition-all"
            />
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
             {/* Filter Check: Advanced or Simple */}
             {mode === 'advanced' && advancedFilterDefs && advancedSortDefs && advancedState && onAdvancedStateChange ? (
               <AdvancedFilterSortPanel 
                 filterDefs={advancedFilterDefs}
                 sortDefs={advancedSortDefs}
                 state={advancedState}
                 onChange={onAdvancedStateChange}
                 onReset={onAdvancedReset || (() => {})}
                 defaultSort={defaultSort}
               />
             ) : (
                /* Simple Filter Dropdown */
                filterParams && filterParams.length > 0 && onFilterChange && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-9 px-4 text-sm text-gray-600 hover:text-gray-900 bg-white border-gray-200 hover:bg-gray-50 rounded-xl"
                      >
                        {getSelectedFilterLabel()}
                        <ChevronDown className="ml-2 h-3.5 w-3.5 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 p-1 rounded-xl border-gray-200 shadow-xl">
                      {filterParams.map((param) => (
                        <DropdownMenuItem
                          key={param.key}
                          onClick={() => onFilterChange(param.value)}
                          className={cn(
                            "text-sm rounded-lg px-3 py-2 cursor-pointer",
                            selectedFilter === param.value 
                              ? "bg-blue-50 text-blue-700 font-medium" 
                              : "text-gray-600 hover:bg-gray-100"
                          )}
                        >
                          {param.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
             )}

            {/* View Mode Toggle */}
            {!hideViewModeToggle && onViewModeChange && (
              <div className="flex items-center bg-gray-100/80 rounded-xl p-1 relative">
                {['grid', 'list'].map((mode) => (
                  <Button
                    key={mode}
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewModeChange(mode as 'grid' | 'list')}
                    className={cn(
                      "h-7 w-8 px-0 rounded-lg transition-colors relative z-10",
                      viewMode === mode 
                        ? "text-blue-600" 
                        : "text-gray-500 hover:text-gray-700"
                    )}
                  >
                    {viewMode === mode && (
                      <motion.div
                        layoutId="desktopViewModeHighlight"
                        className="absolute inset-0 bg-white rounded-lg shadow-sm"
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center justify-center">
                      {mode === 'grid' ? <Grid3X3 className="h-4 w-4" /> : <List className="h-4 w-4" />}
                    </span>
                  </Button>
                ))}
              </div>
            )}

            {/* Multi-select Toggle */}
            {onMultiSelect && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMultiSelectMode(true)}
                className="h-9 w-9 p-0 text-gray-600 bg-white border-gray-200 hover:bg-gray-50 rounded-xl transition-all"
                title="Mehrere auswählen"
              >
                <ListChecks className="h-4 w-4" />
              </Button>
            )}

            {trailing}

            {infoText && (
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {infoText}
              </div>
            )}

            {leading}
          </div>
        </div>
      </div>

       {/* Tablet Layout (md to lg) */}
       <div className="hidden md:block lg:hidden px-4 py-3">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {mode === 'advanced' && advancedFilterDefs && advancedSortDefs && advancedState && onAdvancedStateChange ? (
                 <AdvancedFilterSortPanel 
                   filterDefs={advancedFilterDefs}
                   sortDefs={advancedSortDefs}
                   state={advancedState}
                   onChange={onAdvancedStateChange}
                   onReset={onAdvancedReset || (() => {})}
                   defaultSort={defaultSort}
                 />
              ) : (
                filterParams && filterParams.length > 0 && onFilterChange && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-9 px-3 text-sm text-gray-600 bg-white border-gray-200 rounded-xl"
                      >
                        {getSelectedFilterLabel()}
                        <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-52 p-1 rounded-xl shadow-xl">
                      {filterParams.map((param) => (
                        <DropdownMenuItem
                          key={param.key}
                          onClick={() => onFilterChange(param.value)}
                          className={cn(
                            "text-sm rounded-lg px-3 py-2 cursor-pointer",
                            selectedFilter === param.value 
                              ? "bg-blue-50 text-blue-700 font-medium" 
                              : "text-gray-600 hover:bg-gray-100"
                          )}
                        >
                          {param.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              )}
              {trailing}
            </div>

            <div className="flex items-center gap-3">
              {infoText && (
                <div className="text-xs font-medium text-gray-400">
                  {infoText}
                </div>
              )}

              {!hideViewModeToggle && onViewModeChange && (
                <div className="flex items-center bg-gray-100/80 rounded-xl p-1 relative">
                  {['grid', 'list'].map((mode) => (
                    <Button
                      key={mode}
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewModeChange(mode as 'grid' | 'list')}
                      className={cn(
                        "h-7 w-8 p-0 rounded-lg transition-colors relative z-10",
                        viewMode === mode 
                          ? "text-blue-600" 
                          : "text-gray-500 hover:text-gray-700"
                      )}
                    >
                      {viewMode === mode && (
                        <motion.div
                          layoutId="tabletViewModeHighlight"
                          className="absolute inset-0 bg-white rounded-lg shadow-sm"
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-center">
                        {mode === 'grid' ? <Grid3X3 className="h-4 w-4" /> : <List className="h-4 w-4" />}
                      </span>
                    </Button>
                  ))}
                </div>
              )}

              {/* Multi-select Toggle */}
              {onMultiSelect && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsMultiSelectMode(true)}
                  className="h-9 w-9 p-0 text-gray-600 bg-white border-gray-200 hover:bg-gray-50 rounded-xl transition-all"
                  title="Mehrere auswählen"
                >
                  <ListChecks className="h-4 w-4" />
                </Button>
              )}

              {leading}
            </div>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={searchPlaceholder}
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              className="pl-10 h-10 text-sm bg-gray-50/50 border-gray-200 focus-visible:ring-0 rounded-xl"
            />
          </div>
        </div>
      </div>

       {/* Mobile Layout (sm and below) */}
       <div className="block md:hidden px-4 py-3">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {mode === 'advanced' && advancedFilterDefs && advancedSortDefs && advancedState && onAdvancedStateChange ? (
                 <AdvancedFilterSortPanel 
                   filterDefs={advancedFilterDefs}
                   sortDefs={advancedSortDefs}
                   state={advancedState}
                   onChange={onAdvancedStateChange}
                   onReset={onAdvancedReset || (() => {})}
                   defaultSort={defaultSort}
                 />
              ) : (
                filterParams && filterParams.length > 0 && onFilterChange && (
                  <Sheet open={isMobileFilterOpen} onOpenChange={setIsMobileFilterOpen}>
                    <SheetTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-9 px-4 text-sm bg-white border-gray-200 hover:bg-gray-50 rounded-xl"
                      >
                        <SlidersHorizontal className="h-4 w-4 mr-2 text-gray-500" />
                        Filter
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="h-[70vh] rounded-t-[2.5rem] p-6 border-none shadow-2xl">
                      <SheetHeader className="pb-6 border-b border-gray-100">
                        <SheetTitle className="text-left text-xl font-bold">Filter auswählen</SheetTitle>
                      </SheetHeader>
                      <div className="space-y-2 mt-6 overflow-y-auto max-h-[50vh]">
                         {filterParams.map((param) => (
                          <Button
                            key={param.key}
                            variant={selectedFilter === param.value ? "default" : "ghost"}
                            onClick={() => handleFilterSelect(param.value)}
                            className={cn(
                              "w-full justify-start h-14 text-base rounded-2xl px-5 transition-all",
                              selectedFilter === param.value 
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-200" 
                                : "text-gray-700 hover:bg-gray-100"
                            )}
                          >
                            {param.label}
                          </Button>
                        ))}
                      </div>
                    </SheetContent>
                  </Sheet>
                )
              )}

              {!hideViewModeToggle && onViewModeChange && (
                <div className="flex items-center bg-gray-100/80 rounded-xl p-1 relative">
                  {['grid', 'list'].map((mode) => (
                    <Button
                      key={mode}
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewModeChange(mode as 'grid' | 'list')}
                      className={cn(
                        "h-7 w-8 p-0 rounded-lg transition-colors relative z-10",
                        viewMode === mode 
                          ? "text-blue-600" 
                          : "text-gray-500 hover:text-gray-700"
                      )}
                    >
                      {viewMode === mode && (
                        <motion.div
                          layoutId="mobileViewModeHighlight"
                          className="absolute inset-0 bg-white rounded-lg shadow-sm"
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-center">
                        {mode === 'grid' ? <Grid3X3 className="h-4 w-4" /> : <List className="h-4 w-4" />}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
              
              {/* Multi-select Toggle */}
              {onMultiSelect && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsMultiSelectMode(true)}
                  className="h-9 w-9 p-0 text-gray-600 bg-white border-gray-200 hover:bg-gray-50 rounded-xl transition-all"
                  title="Mehrere auswählen"
                >
                  <ListChecks className="h-4 w-4" />
                </Button>
              )}
              
              {trailing}
            </div>

            <div className="flex items-center gap-3">
              {infoText && (
                <div className="text-xs font-medium text-gray-400 hidden sm:block">
                  {infoText}
                </div>
              )}
              {leading}
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={searchPlaceholder}
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              className="pl-10 h-11 text-base bg-gray-50/50 border-gray-200 focus-visible:ring-0 rounded-2xl shadow-none"
            />
          </div>
        </div>
      </div>
      </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
